import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/Dialog';
import wordIcon from '../assets/word.svg';
import { ImportSourceStep } from './import-signal/ImportSourceStep';
import { ImportUploadStep } from './import-signal/ImportUploadStep';
import { ImportLoadingStep } from './import-signal/ImportLoadingStep';
import { DefaultsStep } from './signal-review/DefaultsStep';
import { IndividualStep } from './signal-review/IndividualStep';
import { SummaryStep } from './signal-review/SummaryStep';
import { useImportFlow } from './import-signal/hooks/useImportFlow';
import { useReviewNavigation } from './import-signal/hooks/useReviewNavigation';
import { useCreateSignals } from './import-signal/hooks/useCreateSignals';
import { SignalReviewProvider } from './import-signal/context/SignalReviewContext';
import { useSignalReview } from './import-signal/context/useSignalReview';

interface ImportSignalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ImportSignalModal = ({
  open,
  onOpenChange,
}: ImportSignalModalProps) => {
  return (
    <SignalReviewProvider>
      <ImportSignalModalContent open={open} onOpenChange={onOpenChange} />
    </SignalReviewProvider>
  );
};

const ImportSignalModalContent = ({
  open,
  onOpenChange,
}: ImportSignalModalProps) => {
  const { t } = useTranslation('knowledge');

  const {
    step,
    direction,
    files,
    defaults,
    setDefaults,
    handleFilesSelected,
    handleRemoveFile,
    handleUpload,
    handleBackToSource,
    handleSourceSelected,
    goToStep,
  } = useImportFlow({ open });

  const {
    signals,
    initializeSignals,
    saveSignal,
    toggleExclude,
    resetSignals,
  } = useSignalReview();

  useEffect(() => {
    if (!open) {
      resetSignals();
    }
  }, [open, resetSignals]);

  const {
    currentSignal,
    currentIndex,
    remainingCount,
    editingFromSummary,
    handleAcceptAll,
    handleReviewIndividually,
    handleNextSignal,
    handleAcceptRemaining,
    handleBackFromIndividual,
    handleEditFromSummary,
    handleBackFromSummary,
    handleCancelEdit,
  } = useReviewNavigation({ open, defaults, goToStep });

  const { isCreating, handleConfirm } = useCreateSignals({
    open,
    onOpenChange,
  });

  const isReviewStep =
    step === 'defaults' || step === 'individual' || step === 'summary';

  const dialogTitle =
    step === 'source' ? t('integrations.title') : t('importModal.title');

  const dialogDescription =
    step === 'source'
      ? t('integrations.subtitle')
      : step === 'upload'
      ? t('importModal.description')
      : t('importModal.reviewDescription');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        showCloseButton
        dismissibleByOutsideClick={false}
        className="p-0"
      >
        {step !== 'loading' && !isReviewStep && (
          <div className="px-10 pt-10">
            <DialogHeader className="gap-4">
              {step !== 'source' && (
                <div className="flex items-center gap-2 mb-2">
                  <img src={wordIcon} alt="Word" className="h-7 w-7" />
                  <span className="text-muted-foreground text-lg">&#8250;</span>
                  <img
                    src="/intera_logo_blue.svg"
                    alt="Intera"
                    className="h-7 w-7"
                  />
                </div>
              )}
              <DialogTitle className="text-2xl">{dialogTitle}</DialogTitle>
              <DialogDescription className="text-base">
                {dialogDescription}
              </DialogDescription>
            </DialogHeader>
          </div>
        )}

        <div
          className={cn(
            'relative',
            step === 'loading'
              ? 'min-h-[500px]'
              : isReviewStep
              ? 'p-6'
              : 'h-[500px]'
          )}
        >
          <div
            key={`${step}-${direction}`}
            className={`
              h-full
              transition-all duration-300 ease-out
              ${
                direction === 'forward'
                  ? 'animate-in slide-in-from-right-8 fade-in'
                  : 'animate-in slide-in-from-left-8 fade-in'
              }
            `}
          >
            {step === 'source' && (
              <ImportSourceStep onSourceSelected={handleSourceSelected} />
            )}

            {step === 'upload' && (
              <ImportUploadStep
                onFilesSelected={handleFilesSelected}
                selectedFiles={files}
                onRemoveFile={handleRemoveFile}
                onNext={() => handleUpload(initializeSignals)}
                hasFiles={files.length > 0}
                onBack={handleBackToSource}
              />
            )}

            {step === 'loading' && <ImportLoadingStep />}

            {step === 'defaults' && (
              <DefaultsStep
                defaults={defaults}
                signalCount={signals.length}
                onDefaultsChange={setDefaults}
                onAcceptAll={handleAcceptAll}
                onReviewIndividually={handleReviewIndividually}
                onCancel={() => onOpenChange(false)}
              />
            )}

            {step === 'individual' && currentSignal && (
              <IndividualStep
                key={currentSignal.index}
                signal={currentSignal}
                totalCount={signals.length}
                remainingCount={remainingCount}
                fromSummary={editingFromSummary}
                onSave={saveSignal}
                onSkip={handleNextSignal}
                onAcceptRemaining={handleAcceptRemaining}
                onBack={handleBackFromIndividual}
                onNext={handleNextSignal}
                onCancel={handleCancelEdit}
                isLast={currentIndex === signals.length - 1}
              />
            )}

            {step === 'summary' && (
              <SummaryStep
                signals={signals}
                onToggleExclude={toggleExclude}
                onEditSignal={handleEditFromSummary}
                onBack={handleBackFromSummary}
                onConfirm={handleConfirm}
                isCreating={isCreating}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
